import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid2";
import { TextField, Button } from "@mui/material";
import { getComparator, handleSort } from "../../hooks/useTable.js";
import { getCallsInfo } from "../../services/logsModule/calllogsServices.js";
import { CALLLOG_ERRORS } from "../../common/constants/logsModule/logsConstants.js";
import {
  SIZES,
  VARIANTS,
  AUTO_COMPLETE,
  SORT_ORDERS,
} from "../../common/constants/commonConstants.js";
import {
  CALLLOG_STRINGS,
  CALLLOG_TABLE_COLUMNS,
  CALLLOG_KEYS,
} from "../../common/constants/logsModule/logsConstants.js";
import CallLogsTable from "./callLogsTable.jsx";

const CallLogs = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [callLog, setCallLog] = useState([]);
  const [searchCall, setSearchCall] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loadingCalls, setLoadingCalls] = useState(true);
  const [order, setOrder] = useState(SORT_ORDERS.ASCENDING);
  const [orderBy, setOrderBy] = useState(CALLLOG_KEYS.CALL_NAME);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filterDates, setFilterDates] = useState({ start: "", end: "" });

  useEffect(() => {
    fetchCalls();
  }, []);

  const fetchCalls = async () => {
    try {
      setLoadingCalls(true);
      setCallLog(await getCallsInfo());
    } catch (error) {
      console.error(CALLLOG_ERRORS.FETCH_CALLS_ERROR, error);
    } finally {
      setLoadingCalls(false);
    }
  };

  // const formatDate = (isoDate) => {
  //   const date = new Date(isoDate);
  //   if (isNaN(date)) return "";
  //   return date.toLocaleString();
  // };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",   // Feb
      day: "2-digit",   // 08
      year: "numeric",  // 2026
      hour: "2-digit",  // 02
      minute: "2-digit",// 00
      hour12: true      // PM format
    });
  };

  // MAIN: compute filtered, sorted, paginated & formatted calls for display
  const { filteredCallsLength, callsForDisplay } = useMemo(() => {
    const searchLower = searchCall.trim().toLowerCase();
    // work on a copy to avoid mutating state
    let filtered = callLog.slice();

    // DATE FILTER: handle start / end separately and create deterministic Date objects
    if (filterDates.start || filterDates.end) {
      const start = filterDates.start
        ? new Date(`${filterDates.start}T00:00:00`)
        : null;
      const end = filterDates.end
        ? new Date(`${filterDates.end}T23:59:59.999`)
        : null;

      filtered = filtered.filter((call) => {
        const logDate = new Date(call.createdAt);
        if (isNaN(logDate)) return false;
        if (start && logDate.getTime() < start.getTime()) return false;
        if (end && logDate.getTime() > end.getTime()) return false;
        return true;
      });
    }

    // SEARCH FILTER (agent/customer/callId)
    if (searchLower) {
      filtered = filtered.filter((call) => {
        const agent = (call.agentfullname || "").toLowerCase();
        const customer = (call.customerfullname || "").toLowerCase();
        const callId = (call.callconnectionid || "").toLowerCase();
        return (
          agent.includes(searchLower) ||
          customer.includes(searchLower) ||
          callId.includes(searchLower)
        );
      });
    }

    const filteredLength = filtered.length;

    // SORT (make a copy before sort to be safe)
    const sorted = filtered.slice().sort(getComparator(order, orderBy));

    // PAGINATE
    const startIdx = page * rowsPerPage;
    const paged = sorted.slice(startIdx, startIdx + rowsPerPage);

    // FORMAT createdAt for display AFTER sorting (so sort uses original ISO dates)
    const formatted = paged.map((call) => ({
      ...call,
      createdAt: call.createdAt ? formatDate(call.createdAt) : "",
    }));

    return {
      filteredCallsLength: filteredLength,
      callsForDisplay: formatted,
    };
  }, [callLog, searchCall, filterDates, order, orderBy, page, rowsPerPage]);

  const handleRequestSort = (property) => {
    handleSort(order, orderBy, property, setOrder, setOrderBy);
  };

  const handleFilter = () => {
    // set filter dates and reset to first page so we don't end up on an empty page
    setFilterDates({ start: startDate, end: endDate });
    setPage(0);
  };

  const handleClear = () => {
    setStartDate("");
    setEndDate("");
    setFilterDates({ start: "", end: "" });
  };

  // Reset to first page whenever search text changes (optional UX improvement)
  useEffect(() => {
    setPage(0);
  }, [searchCall, rowsPerPage, order, orderBy]);

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid xs={12} sm={3}>
          <TextField
            label="Start Date"
            type="date"
            size={SIZES.SMALL}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid xs={12} sm={3}>
          <TextField
            label="End Date"
            type="date"
            size={SIZES.SMALL}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Grid>
        <Grid xs={12} sm={2} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant={VARIANTS.CONTAINED}
            onClick={handleFilter}
            fullWidth
          >
            Submit
          </Button>
          <Button
            variant={VARIANTS.OUTLINED}
            onClick={handleClear}
            fullWidth
          >
            Clear
          </Button>
        </Grid>
        <Grid xs={12} sm={4} sx={{flex:1}}>
          <TextField
            fullWidth
            size={SIZES.SMALL}
            value={searchCall}
            variant={VARIANTS.OUTLINED}
            autoComplete={AUTO_COMPLETE.OFF}
            label={CALLLOG_STRINGS.SEARCH_CALLS}
            onChange={(e) => setSearchCall(e.target.value)}
          />
        </Grid>
      </Grid>
      <CallLogsTable
        page={page}
        order={order}
        setPage={setPage}
        orderBy={orderBy}
        calls={callsForDisplay}
        isLoading={loadingCalls}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        columns={CALLLOG_TABLE_COLUMNS}
        handleRequestSort={handleRequestSort}
        filteredCallsLength={filteredCallsLength}
      />
    </>
  );
};

export default CallLogs;
